/**
 * Unified Operator Workspace — full context panel edition
 *
 * Layout: [420px list] | [260px context panel] | [flex thread + reply]
 *
 * Sections:
 *   Handoffs   — escalated conversations requiring human intervention
 *   Chats      — all AI conversations (read-only monitor / intervene)
 *   Approvals  — skill executions waiting for operator sign-off
 */
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useRef, useEffect } from 'react';
import {
  handoffApi, conversationApi, approvalApi, toolApi,
  type Handoff, type HandoffMessage,
  type Conversation, type ConversationDetail, type ConversationEvent,
  type ApprovalRequest, type ToolDefinition,
} from '@/lib/api';
import { useAdminSocket } from '@/hooks/useAdminSocket';
import { useAuth } from '@/contexts/AuthContext';
import { usePortalSocketEvent } from '@/contexts/PortalSocketContext';
import {
  User, Bot, Send, CheckCircle2, XCircle,
  Inbox, Wifi, WifiOff, UserCheck, UserPlus,
  Tag, MessageSquare, AlertTriangle,
  ChevronDown, ChevronRight, Zap, Play, Loader2, Sparkles, X,
} from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/components/ui/toast';

export const Route = createFileRoute('/_auth/inbox')({ component: InboxPage });

/* ─────────────────────────── constants ─── */
const PRIORITY_ORDER: Record<string, number> = { URGENT: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
const PRI: Record<string, { dot: string; label: string; bg: string; text: string }> = {
  URGENT: { dot: '#ef4444', label: 'Urgent',  bg: '#3a0d0d', text: '#fca5a5' },
  HIGH:   { dot: '#f97316', label: 'High',    bg: '#321a06', text: '#fdba74' },
  MEDIUM: { dot: '#f59e0b', label: 'Medium',  bg: '#2a1f06', text: '#fde68a' },
  LOW:    { dot: '#64748b', label: 'Low',     bg: '#111827', text: '#94a3b8' },
};

type Section = 'handoffs' | 'chats' | 'approvals';
type HandoffTab = 'open' | 'mine' | 'assigned' | 'resolved';

/* ─────────────────────────── helpers ─── */
function Avatar({ name, email, size = 28, color = '#4f46e5' }: { name?: string | null; email?: string; size?: number; color?: string }) {
  const txt = (name || email || '?').split(/[\s@]/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join('');
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: color, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.36, fontWeight: 700, color: '#fff',
    }}>{txt}</div>
  );
}

function SenderBubble({ msg }: { msg: HandoffMessage }) {
  const isUser = msg.senderType === 'USER';
  const isOp   = msg.senderType === 'OPERATOR';
  return (
    <div style={{ display: 'flex', flexDirection: isUser ? 'row' : 'row-reverse', gap: 8, alignItems: 'flex-end', marginBottom: 10 }}>
      <div style={{
        width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
        background: isUser ? '#1e3a5f' : isOp ? '#0d2d22' : '#2a1a00',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {isUser ? <User size={11} style={{ color: '#60a5fa' }} /> :
         isOp   ? <UserCheck size={11} style={{ color: '#34d399' }} /> :
                  <Bot size={11} style={{ color: '#fbbf24' }} />}
      </div>
      <div style={{ maxWidth: '68%' }}>
        <div style={{
          padding: '8px 12px',
          borderRadius: isUser ? '14px 14px 14px 3px' : '14px 14px 3px 14px',
          background: isUser ? '#1e293b' : isOp ? '#0d2d22' : '#1a1a2e',
          border: `1px solid ${isUser ? '#334155' : isOp ? '#10b98130' : '#312e81'}`,
          fontSize: 13, color: '#e2e8f0', lineHeight: 1.55, wordBreak: 'break-word',
        }}>{msg.content}</div>
        <div style={{ fontSize: 10, color: '#475569', marginTop: 2, textAlign: isUser ? 'left' : 'right' }}>
          {isOp ? 'You' : isUser ? 'Customer' : 'AI Agent'} · {new Date(msg.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  );
}


/* ── Humanisation helpers ── */

/** "check_shipping_status" → "Check Shipping Status" */
function toTitle(s: string) {
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/** "externalUserId" / "external_user_id" → "External User ID" */
function humanKey(k: string) {
  return k
    .replace(/([a-z])([A-Z])/g, '$1 $2')   // camelCase split
    .replace(/_/g, ' ')                      // snake_case split
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\bId\b/g, 'ID')
    .replace(/\bMs\b/g, 'ms')
    .replace(/\bUrl\b/g, 'URL');
}

/** Format a payload value as a readable string */
function humanValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') return v.toLocaleString();
  if (typeof v === 'string') {
    // ISO date? → locale date
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) {
      try { return new Date(v).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); } catch { /* */ }
    }
    return v;
  }
  if (Array.isArray(v)) return v.map(x => humanValue(x)).join(', ');
  return JSON.stringify(v);
}

/** Keys we skip in the payload card (internal / noise) */
const SKIP_KEYS = new Set(['toolName', 'skillName', 'eventType', 'sequenceNumber', 'injectedContextKeys']);

/** Keys that are transparent wrappers — their children bubble up to root level */
const INLINE_KEYS = new Set(['result', 'params', 'output', 'data', 'input']);

/** Flat key-value pairs extracted from an arbitrary payload object */
function flattenPayload(obj: Record<string, unknown>, prefix = ''): Array<{ key: string; value: string }> {
  const out: Array<{ key: string; value: string }> = [];
  for (const [k, v] of Object.entries(obj)) {
    if (SKIP_KEYS.has(k)) continue;

    // Transparent wrapper keys: inline their children at the current level
    const isWrapper = !prefix && INLINE_KEYS.has(k);
    const label = isWrapper ? '' : prefix ? `${humanKey(prefix)} › ${humanKey(k)}` : humanKey(k);

    // Try to parse JSON strings into sub-objects
    let parsed: unknown = v;
    if (typeof v === 'string' && (v.startsWith('{') || v.startsWith('['))) {
      try { parsed = JSON.parse(v); } catch { /* leave as string */ }
    }

    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      // Recurse — use the wrapper key as new prefix (unless it's a transparent key)
      out.push(...flattenPayload(parsed as Record<string, unknown>, isWrapper ? '' : k));
    } else if (Array.isArray(parsed)) {
      // Arrays: join simple values; expand objects
      const simplified = (parsed as unknown[]).every(x => typeof x !== 'object' || x === null);
      if (simplified) {
        out.push({ key: label || humanKey(k), value: (parsed as unknown[]).map(x => humanValue(x)).join(', ') });
      } else {
        // Expand each array item
        (parsed as unknown[]).forEach((item, i) => {
          if (item !== null && typeof item === 'object') {
            out.push(...flattenPayload(item as Record<string, unknown>, `${humanKey(k)} ${i + 1}`));
          } else {
            out.push({ key: `${humanKey(k)} ${i + 1}`, value: humanValue(item) });
          }
        });
      }
    } else {
      out.push({ key: label || humanKey(k), value: humanValue(parsed) });
    }
  }
  return out;
}

const EVENT_META: Record<string, { label: string; status: string; statusColor: string }> = {
  SKILL_SELECTED:              { label: 'Skill selected',        status: 'AI decided to act',               statusColor: '#818cf8' },
  SKILL_EXECUTION_STARTED:     { label: 'Action started',        status: 'Running in the background…',      statusColor: '#818cf8' },
  SKILL_EXECUTION_COMPLETED:   { label: 'Action completed',      status: 'Finished successfully',           statusColor: '#34d399' },
  SKILL_EXECUTION_FAILED:      { label: 'Action failed',         status: 'Something went wrong',            statusColor: '#ef4444' },
  SKILL_APPROVAL_REQUESTED:    { label: 'Approval needed',       status: 'Waiting for operator sign-off',   statusColor: '#f59e0b' },
  SKILL_APPROVAL_APPROVED:     { label: 'Approved',              status: 'Operator approved the action',    statusColor: '#34d399' },
  SKILL_APPROVAL_REJECTED:     { label: 'Rejected',              status: 'Operator rejected the action',    statusColor: '#ef4444' },
  SKILL_APPROVAL_TIMED_OUT:    { label: 'Approval timed out',    status: 'No response within time limit',   statusColor: '#f97316' },
  GUARDRAIL_TRIGGERED:         { label: 'Safety check triggered', status: 'AI response was blocked',        statusColor: '#f97316' },
  HANDOFF_CREATED:             { label: 'Escalated to operator', status: '',                                statusColor: '#6366f1' },
  CONVERSATION_STARTED:        { label: 'Conversation started',  status: '',                                statusColor: '#475569' },
};

/** Accordion for a ConversationEvent (tool call, guardrail, etc.) */
function EventAccordion({ event }: { event: ConversationEvent }) {
  const [open, setOpen] = useState(false);
  const meta    = EVENT_META[event.eventType];
  const payload = event.payload as Record<string, unknown> | null;
  const isHandoff = event.eventType === 'HANDOFF_CREATED';
  const isStart   = event.eventType === 'CONVERSATION_STARTED';

  /* Divider-style events — no accordion needed */
  if (isHandoff || isStart) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0', padding: '0 4px' }}>
        <div style={{ flex: 1, height: 1, background: isHandoff ? '#2d3f5f' : '#1e2840' }} />
        <span style={{ fontSize: 10, color: isHandoff ? '#475569' : '#334155', whiteSpace: 'nowrap' }}>
          {isHandoff ? '↑ Escalated to operator' : 'Conversation started'}
        </span>
        <div style={{ flex: 1, height: 1, background: isHandoff ? '#2d3f5f' : '#1e2840' }} />
      </div>
    );
  }

  const isGuardrail  = event.eventType === 'GUARDRAIL_TRIGGERED';
  const isApproval   = event.eventType.startsWith('SKILL_APPROVAL');
  const isSkillFail  = event.eventType === 'SKILL_EXECUTION_FAILED';
  const isSkillDone  = event.eventType === 'SKILL_EXECUTION_COMPLETED';
  const color = meta?.statusColor ?? (isGuardrail ? '#f97316' : isSkillFail ? '#ef4444' : isSkillDone ? '#34d399' : isApproval ? '#f59e0b' : '#818cf8');

  /* Human-readable tool / skill name */
  const rawName = (payload?.toolName ?? payload?.skillName ?? '') as string;
  const displayName = rawName ? toTitle(rawName) : (meta?.label ?? toTitle(event.eventType));

  /* Duration */
  const durationLabel = event.durationMs != null
    ? event.durationMs < 1000 ? `${event.durationMs}ms` : `${(event.durationMs / 1000).toFixed(1)}s`
    : null;

  /* Flat key-value pairs for the expanded view */
  const pairs = payload ? flattenPayload(payload) : [];

  return (
    <div style={{ margin: '4px 0', borderRadius: 8, border: `1px solid ${color}20`, background: `${color}06`, overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 7, padding: '7px 10px',
        background: 'none', border: 'none', cursor: pairs.length > 0 ? 'pointer' : 'default', textAlign: 'left',
      }}>
        {/* Status dot */}
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#e2e8f0', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
            {displayName}
          </div>
          {meta?.status && (
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 1 }}>{meta.status}</div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
          {durationLabel && <span style={{ fontSize: 9, color: '#334155', background: '#1e2840', padding: '1px 5px', borderRadius: 4 }}>{durationLabel}</span>}
          {pairs.length > 0 && (
            open ? <ChevronDown size={11} style={{ color: '#475569' }} /> : <ChevronRight size={11} style={{ color: '#475569' }} />
          )}
        </div>
      </button>

      {open && pairs.length > 0 && (
        <div style={{ padding: '0 10px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {pairs.map(({ key, value }, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', padding: '4px 8px', borderRadius: 6, background: '#0a0e1a', border: '1px solid #1a2035' }}>
              <span style={{ fontSize: 10, color: '#475569', flexShrink: 0, minWidth: 90, fontWeight: 600, paddingTop: 1 }}>{key}</span>
              <span style={{ fontSize: 11, color: '#cbd5e1', lineHeight: 1.45, wordBreak: 'break-word', flex: 1 }}>{value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Merges messages + events into a single chronological timeline */
function buildTimeline(messages: HandoffMessage[], events: ConversationEvent[]) {
  type TimelineItem = ({ kind: 'msg' } & HandoffMessage) | ({ kind: 'event' } & ConversationEvent);
  const items: TimelineItem[] = [
    ...messages.map(m => ({ kind: 'msg' as const, ...m })),
    ...events.map(e => ({ kind: 'event' as const, ...e })),
  ];
  return items.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}

function waitLabel(createdAt: string) {
  const mins = Math.floor((Date.now() - new Date(createdAt).getTime()) / 60000);
  const s = mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
  const color = mins > 30 ? '#ef4444' : mins > 10 ? '#f59e0b' : '#34d399';
  return { s, color };
}

/* ─────────────────────────── Operator Actions panel ─── */
function OperatorActionsPanel({ tools }: { tools: ToolDefinition[] }) {
  const [activeToolId, setActiveToolId] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [invoking, setInvoking] = useState(false);
  const [results, setResults] = useState<Record<string, unknown>>({});

  const activeTool = tools.find(t => t.id === activeToolId);
  const schema = activeTool?.inputSchema as { properties?: Record<string, { type: string; description?: string; enum?: string[]; default?: unknown }> } | undefined;
  const required = (activeTool?.inputSchema as { required?: string[] } | undefined)?.required ?? [];

  async function invoke() {
    if (!activeTool) return;
    setInvoking(true);
    try {
      const params: Record<string, unknown> = {};
      Object.entries(formValues).forEach(([k, v]) => { if (v) params[k] = v; });
      const result = await toolApi.test(activeTool.id, params);
      setResults(r => ({ ...r, [activeTool.id]: result }));
      toast.success('Action completed');
    } catch (e) {
      toast.error(`Failed: ${(e as Error).message}`);
    } finally {
      setInvoking(false);
    }
  }

  return (
    <div style={{ padding: '0 12px 12px', flex: 1, overflowY: 'auto' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 0 6px', display: 'flex', alignItems: 'center', gap: 5 }}>
        <Zap size={10} /> Admin Actions
      </div>
      {tools.map(tool => {
        const isActive = activeToolId === tool.id;
        const result = results[tool.id] as { result?: unknown } | undefined;
        return (
          <div key={tool.id} style={{ marginBottom: 6, borderRadius: 8, border: `1px solid ${isActive ? '#4f46e580' : '#1e2840'}`, background: isActive ? '#0d1030' : '#0f1221', overflow: 'hidden' }}>
            <button onClick={() => { setActiveToolId(isActive ? null : tool.id); setFormValues({}); }} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 7, padding: '7px 10px',
              background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
            }}>
              <span style={{ fontSize: 12 }}>{tool.operatorLabel?.split(' ')[0] ?? '⚙'}</span>
              <span style={{ flex: 1, fontSize: 11, fontWeight: 600, color: isActive ? '#a5b4fc' : '#cbd5e1' }}>
                {tool.operatorLabel?.replace(/^\S+\s/, '') ?? tool.displayName}
              </span>
              {isActive ? <ChevronDown size={11} style={{ color: '#6366f1' }} /> : <ChevronRight size={11} style={{ color: '#475569' }} />}
            </button>

            {isActive && (
              <div style={{ padding: '0 10px 10px' }}>
                <p style={{ fontSize: 10, color: '#475569', margin: '0 0 8px', lineHeight: 1.5 }}>{tool.description}</p>
                {schema?.properties && Object.entries(schema.properties).map(([key, prop]) => (
                  <div key={key} style={{ marginBottom: 6 }}>
                    <label style={{ fontSize: 10, color: '#64748b', display: 'block', marginBottom: 3, fontWeight: 600 }}>
                      {key}{required.includes(key) ? ' *' : ''}
                    </label>
                    {prop.enum ? (
                      <select value={formValues[key] ?? ''} onChange={e => setFormValues(v => ({ ...v, [key]: e.target.value }))}
                        style={{ width: '100%', padding: '5px 8px', background: '#161b2e', border: '1px solid #2d3f5f', borderRadius: 6, color: '#e2e8f0', fontSize: 11 }}>
                        <option value="">— select —</option>
                        {prop.enum.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    ) : (
                      <input type={prop.type === 'number' ? 'number' : 'text'}
                        placeholder={prop.description ?? key}
                        value={formValues[key] ?? ''}
                        onChange={e => setFormValues(v => ({ ...v, [key]: e.target.value }))}
                        style={{ width: '100%', padding: '5px 8px', background: '#161b2e', border: '1px solid #2d3f5f', borderRadius: 6, color: '#e2e8f0', fontSize: 11, boxSizing: 'border-box' }} />
                    )}
                  </div>
                ))}
                <button onClick={invoke} disabled={invoking} style={{
                  width: '100%', marginTop: 6, padding: '6px 0', borderRadius: 7, border: 'none',
                  background: '#4f46e5', color: '#fff', fontSize: 11, fontWeight: 700,
                  cursor: invoking ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                }}>
                  {invoking ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Play size={11} />}
                  {invoking ? 'Running…' : 'Run'}
                </button>
                {result && (
                  <div style={{ marginTop: 8, padding: '6px 8px', background: '#0a0e1a', borderRadius: 6, border: '1px solid #10b98130' }}>
                    <div style={{ fontSize: 9, color: '#34d399', fontWeight: 700, marginBottom: 4, textTransform: 'uppercase' }}>✓ Result</div>
                    <pre style={{ margin: 0, fontSize: 10, color: '#94a3b8', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(typeof result === 'object' && 'result' in result ? (result as { result: unknown }).result : result, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─────────────────────────── page ─── */
export default function InboxPage() {
  const { platformUser } = useAuth();
  const qc = useQueryClient();
  const myId = platformUser?.id;

  const [section, setSection]             = useState<Section>('handoffs');
  const [handoffTab, setHandoffTab]       = useState<HandoffTab>('open');
  const [selectedHandoffId, setSelectedHandoffId] = useState<string | null>(null);
  const [selectedConvId,    setSelectedConvId]    = useState<string | null>(null);
  const [selectedApprovalId, setSelectedApprovalId] = useState<string | null>(null);

  const [reply,   setReply]   = useState('');
  const [sending, setSending] = useState(false);
  const [assisting, setAssisting] = useState(false);
  const [assistDraft, setAssistDraft] = useState<string | null>(null);
  const [assistChunks, setAssistChunks] = useState(0);
  const [unreadIds, setUnreadIds] = useState<Set<string>>(new Set());
  const [unreadChatIds, setUnreadChatIds] = useState<Set<string>>(new Set());
  const [localMessages, setLocalMessages] = useState<Record<string, HandoffMessage[]>>({});
  const [creatingHandoff, setCreatingHandoff] = useState(false);
  const [handoffNote, setHandoffNote] = useState('');
  const [showHandoffForm, setShowHandoffForm] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  /* ── queries ── */
  const { data: allHandoffs = [], isLoading: loadingHandoffs } = useQuery<Handoff[]>({
    queryKey: ['inbox-handoffs'],
    queryFn: () => handoffApi.list(),
    refetchInterval: 5_000,
  });
  const { data: allChats = [], isLoading: loadingChats } = useQuery<Conversation[]>({
    queryKey: ['inbox-chats'],
    queryFn: () => conversationApi.list({ limit: 50 }),
    // Always fetch so badge count is accurate on all tabs
    refetchInterval: 20_000,
  });
  const { data: convDetail } = useQuery<ConversationDetail>({
    queryKey: ['inbox-conv-detail', selectedConvId],
    queryFn: () => conversationApi.get(selectedConvId!),
    enabled: !!selectedConvId && section === 'chats',
    refetchInterval: 8_000,
  });
  const { data: approvals = [], isLoading: loadingApprovals } = useQuery<ApprovalRequest[]>({
    queryKey: ['inbox-approvals'],
    queryFn: () => approvalApi.list(),
    enabled: section === 'approvals',
  });
  const { data: operatorTools = [] } = useQuery<ToolDefinition[]>({
    queryKey: ['operator-tools'],
    queryFn: () => toolApi.operatorActions(),
  });

  /* ── realtime ── */
  const { connected } = useAdminSocket({
    onMessage: (event) => {
      setLocalMessages(prev => ({
        ...prev,
        [event.conversationId]: [...(prev[event.conversationId] ?? []), event.message as HandoffMessage],
      }));
      // Always refresh the handoff list so new messages appear in the thread immediately
      qc.invalidateQueries({ queryKey: ['inbox-handoffs'] });
      // Also refresh the handoff conversation detail if open
      qc.invalidateQueries({ queryKey: ['inbox-handoff-conv', event.conversationId] });
      // Refresh the chat detail view if it's the open conversation
      if (event.conversationId === selectedConvId && section === 'chats') {
        qc.invalidateQueries({ queryKey: ['inbox-conv-detail', selectedConvId] });
      }
      // Unread for handoffs
      const h = allHandoffs.find(x => x.conversationId === event.conversationId);
      if (h && h.id !== selectedHandoffId) setUnreadIds(prev => new Set([...prev, h.id]));
      // Unread for chats
      if (event.conversationId !== selectedConvId) {
        setUnreadChatIds(prev => new Set([...prev, event.conversationId]));
      }
    },

    onHandoffCreated: () => {
      // New handoff — must refetch to get the full record
      qc.invalidateQueries({ queryKey: ['inbox-handoffs'] });
      qc.invalidateQueries({ queryKey: ['inbox-chats'] });
    },

    onHandoffAssigned: (event) => {
      // Instant optimistic update — patch only the changed handoff in the cache
      if (event.handoff) {
        qc.setQueryData<Handoff[]>(['inbox-handoffs'], prev =>
          prev
            ? prev.map(h => h.id === event.handoffId ? { ...h, ...(event.handoff as Partial<Handoff>) } : h)
            : prev
        );
      }
      // Background sync for eventual consistency
      qc.invalidateQueries({ queryKey: ['inbox-handoffs'] });
      // Invalidate the detail view if it's open
      if (event.handoffId === selectedHandoffId) {
        const convId = allHandoffs.find(h => h.id === event.handoffId)?.conversationId;
        if (convId) qc.invalidateQueries({ queryKey: ['inbox-handoff-conv', convId] });
      }
    },

    onHandoffResolved: (event) => {
      // Instant optimistic update — mark as RESOLVED in the cache immediately
      qc.setQueryData<Handoff[]>(['inbox-handoffs'], prev =>
        prev
          ? prev.map(h => h.id === event.handoffId ? { ...h, status: 'RESOLVED' as const } : h)
          : prev
      );
      // Background sync
      qc.invalidateQueries({ queryKey: ['inbox-handoffs'] });
    },
  });
  usePortalSocketEvent('counts:update', () => { qc.invalidateQueries({ queryKey: ['inbox-approvals'] }); qc.invalidateQueries({ queryKey: ['inbox-chats'] }); });
  usePortalSocketEvent('approval:new', () => qc.invalidateQueries({ queryKey: ['inbox-approvals'] }));
  usePortalSocketEvent('conversation:new', () => { qc.invalidateQueries({ queryKey: ['inbox-chats'] }); });

  // Clear chat unread when that conversation is selected
  useEffect(() => {
    if (selectedConvId) setUnreadChatIds(prev => { const n = new Set(prev); n.delete(selectedConvId); return n; });
  }, [selectedConvId]);

  /* ── derived data ── */
  const filteredHandoffs = allHandoffs
    .filter(h => {
      if (handoffTab === 'open')     return h.status === 'OPEN';
      if (handoffTab === 'mine')     return h.status === 'ASSIGNED' && h.assignedToId === myId;
      if (handoffTab === 'assigned') return h.status === 'ASSIGNED';
      if (handoffTab === 'resolved') return h.status === 'RESOLVED';
      return true;
    })
    .sort((a, b) =>
      (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9) ||
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

  const openCount        = allHandoffs.filter(h => h.status === 'OPEN').length;
  const pendingApprovals = approvals.filter(a => a.status === 'PENDING').length;

  const selectedHandoff = allHandoffs.find(h => h.id === selectedHandoffId) ?? null;
  const convId = selectedHandoff?.conversation?.id ?? selectedHandoff?.conversationId;
  const baseMessages: HandoffMessage[] = selectedHandoff?.conversation?.messages?.slice().reverse() ?? [];
  const extra: HandoffMessage[] = convId ? (localMessages[convId] ?? []) : [];
  const allMessages = [...baseMessages, ...extra]
    .filter((m, i, arr) => arr.findIndex(x => x.id === m.id) === i)
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  // Fetch full conversation detail for the selected handoff (to get events)
  const { data: handoffConvDetail } = useQuery<ConversationDetail>({
    queryKey: ['inbox-handoff-conv', convId],
    queryFn: () => conversationApi.get(convId!),
    enabled: !!convId && !!selectedHandoffId,
  });
  const handoffEvents: ConversationEvent[] = handoffConvDetail?.events ?? [];
  const timeline = buildTimeline(allMessages, handoffEvents);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [timeline.length]);
  useEffect(() => {
    if (selectedHandoffId) setUnreadIds(prev => { const n = new Set(prev); n.delete(selectedHandoffId); return n; });
  }, [selectedHandoffId]);

  const selectedApproval = approvals.find(a => a.id === selectedApprovalId) ?? null;

  /* ── actions ── */
  async function sendReply() {
    if (!reply.trim() || !selectedHandoff?.id) return;
    setSending(true);
    try {
      const msg = await handoffApi.sendMessage(selectedHandoff.id, reply.trim());
      setReply('');
      setAssistDraft(null);
      if (convId) setLocalMessages(prev => ({ ...prev, [convId]: [...(prev[convId] ?? []), msg as unknown as HandoffMessage] }));
    } catch { toast.error('Failed to send'); }
    finally { setSending(false); }
  }

  async function handleAssist() {
    if (!selectedHandoff?.id || assisting) return;
    setAssisting(true);
    setAssistDraft(null);
    try {
      const result = await handoffApi.assist(selectedHandoff.id);
      if (result.draft) {
        setAssistDraft(result.draft);
        setAssistChunks(result.kbChunksUsed);
      } else {
        toast.error('AI could not generate a draft — no relevant content found');
      }
    } catch {
      toast.error('AI Assist failed');
    } finally {
      setAssisting(false);
    }
  }

  async function sendDraftDirectly() {
    if (!assistDraft || !selectedHandoff?.id) return;
    const draft = assistDraft;
    setAssistDraft(null);
    setSending(true);
    try {
      const msg = await handoffApi.sendMessage(selectedHandoff.id, draft);
      if (convId) setLocalMessages(prev => ({ ...prev, [convId]: [...(prev[convId] ?? []), msg as unknown as HandoffMessage] }));
    } catch { toast.error('Failed to send'); }
    finally { setSending(false); }
  }

  async function claimHandoff(id: string) {
    // Optimistic: move to ASSIGNED + set assignedToId immediately
    qc.setQueryData<Handoff[]>(['inbox-handoffs'], prev =>
      prev ? prev.map(h => h.id === id ? { ...h, status: 'ASSIGNED' as const, assignedToId: myId ?? null } : h) : prev
    );
    try {
      const updated = await handoffApi.claim(id);
      // Merge the server response (which has the full assignedTo object)
      qc.setQueryData<Handoff[]>(['inbox-handoffs'], prev =>
        prev ? prev.map(h => h.id === id ? { ...h, ...updated } : h) : prev
      );
      toast.success('Claimed');
    } catch {
      // Roll back on failure
      qc.invalidateQueries({ queryKey: ['inbox-handoffs'] });
      toast.error('Failed to claim');
    }
  }

  async function resolveHandoff(id: string) {
    // Optimistic: mark RESOLVED immediately
    qc.setQueryData<Handoff[]>(['inbox-handoffs'], prev =>
      prev ? prev.map(h => h.id === id ? { ...h, status: 'RESOLVED' as const } : h) : prev
    );
    setSelectedHandoffId(null);
    try {
      await handoffApi.resolve(id);
      toast.success('Resolved');
    } catch {
      qc.invalidateQueries({ queryKey: ['inbox-handoffs'] });
      toast.error('Failed to resolve');
    }
  }

  async function releaseHandoff(id: string) {
    // Optimistic: remove from active list
    qc.setQueryData<Handoff[]>(['inbox-handoffs'], prev =>
      prev ? prev.map(h => h.id === id ? { ...h, status: 'RESOLVED' as const } : h) : prev
    );
    setSelectedHandoffId(null);
    try {
      await handoffApi.release(id);
      toast.success('Released back to AI');
    } catch {
      qc.invalidateQueries({ queryKey: ['inbox-handoffs'] });
      toast.error('Failed to release');
    }
  }

  async function approveRequest(id: string) {
    try { await approvalApi.approve(id); qc.invalidateQueries({ queryKey: ['inbox-approvals'] }); toast.success('Approved'); }
    catch { toast.error('Failed'); }
  }

  async function rejectRequest(id: string) {
    try { await approvalApi.reject(id, 'Rejected by operator'); qc.invalidateQueries({ queryKey: ['inbox-approvals'] }); toast.success('Rejected'); }
    catch { toast.error('Failed'); }
  }

  async function createHandoffFromChat() {
    if (!selectedConvId) return;
    setCreatingHandoff(true);
    try {
      const h = await handoffApi.create(selectedConvId, handoffNote.trim() || undefined, 'MEDIUM');
      toast.success('Handoff created — switching to Handoffs tab');
      setShowHandoffForm(false);
      setHandoffNote('');
      // Refresh data and switch to the new handoff
      qc.invalidateQueries({ queryKey: ['inbox-handoffs'] });
      qc.invalidateQueries({ queryKey: ['inbox-conv-detail', selectedConvId] });
      setSection('handoffs');
      setSelectedHandoffId(h.id);
    } catch (e) {
      toast.error(`Failed: ${(e as Error).message}`);
    } finally {
      setCreatingHandoff(false);
    }
  }

  /* ─────────────── render ─────────────── */
  const pri = selectedHandoff ? (PRI[selectedHandoff.priority] ?? PRI.MEDIUM) : null;
  const isMine = selectedHandoff?.assignedToId === myId;
  const isAssignedToOther = selectedHandoff?.status === 'ASSIGNED' && !isMine;
  const isResolved = selectedHandoff?.status === 'RESOLVED';

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 48px)', minHeight: 0, overflow: 'hidden', padding: 0 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '300px 220px 1fr', gridTemplateRows: '1fr', flex: 1, minHeight: 0, borderRadius: 12, overflow: 'hidden', border: '1px solid #1e2840', alignItems: 'stretch' }}>

        {/* ══════════════ LEFT — list ══════════════ */}
        <div style={{ display: 'flex', flexDirection: 'column', background: '#0f1221', borderRight: '1px solid #1e2840', minHeight: 0, overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ padding: '12px 14px 0', borderBottom: '1px solid #1e2840' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Inbox size={14} style={{ color: '#6366f1' }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>Operator Workspace</span>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: connected ? '#34d399' : '#ef4444' }}>
                {connected ? <Wifi size={10} /> : <WifiOff size={10} />}
                {connected ? 'Live' : '…'}
              </div>
            </div>
            {/* Section tabs */}
            <div style={{ display: 'flex', gap: 2 }}>
              {([
                { id: 'handoffs'  as Section, label: 'Handoffs',  count: openCount,                         urgentColor: '#ef4444' },
                { id: 'chats'     as Section, label: 'Chats',     count: unreadChatIds.size || allChats.filter(c => c.status === 'WAITING_FOR_AGENT').length, urgentColor: '#6366f1' },
                { id: 'approvals' as Section, label: 'Approvals', count: pendingApprovals,                  urgentColor: '#f59e0b' },
              ]).map(tab => (
                <button key={tab.id} onClick={() => setSection(tab.id)} style={{
                  flex: 1, padding: '6px 2px 7px', borderRadius: '6px 6px 0 0',
                  border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 700,
                  background: section === tab.id ? '#1e2840' : 'transparent',
                  color: section === tab.id ? '#e2e8f0' : '#475569',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                  borderBottom: section === tab.id ? '2px solid #6366f1' : '2px solid transparent',
                }}>
                  {tab.label}
                  {tab.count > 0 && <span style={{ fontSize: 9, fontWeight: 800, background: tab.urgentColor, color: '#fff', borderRadius: 20, padding: '1px 5px' }}>{tab.count}</span>}
                </button>
              ))}
            </div>
          </div>

          {/* ── HANDOFFS ── */}
          {section === 'handoffs' && (
            <>
              <div style={{ display: 'flex', padding: '5px 10px', borderBottom: '1px solid #1e2840', gap: 1 }}>
                {(['open', 'mine', 'assigned', 'resolved'] as HandoffTab[]).map(t => (
                  <button key={t} onClick={() => setHandoffTab(t)} style={{
                    padding: '3px 8px', borderRadius: 5, border: 'none', cursor: 'pointer',
                    fontSize: 9, fontWeight: 700, textTransform: 'capitalize',
                    background: handoffTab === t ? '#2d3f5f' : 'transparent',
                    color: handoffTab === t ? '#a5b4fc' : '#475569',
                  }}>
                    {t === 'open' ? `Open${openCount ? ` (${openCount})` : ''}` : t === 'mine' ? 'Mine' : t === 'assigned' ? 'All assigned' : 'Resolved'}
                  </button>
                ))}
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {loadingHandoffs && <div style={{ padding: 24, textAlign: 'center' }}><Spinner size="sm" /></div>}
                {!loadingHandoffs && filteredHandoffs.length === 0 && (
                  <div style={{ padding: '32px 16px', textAlign: 'center', color: '#475569', fontSize: 11 }}>
                    <CheckCircle2 size={20} style={{ opacity: 0.2, display: 'block', margin: '0 auto 8px' }} />
                    {handoffTab === 'open' ? 'No open handoffs' : 'Nothing here'}
                  </div>
                )}
                {filteredHandoffs.map(item => {
                  const p = PRI[item.priority] ?? PRI.MEDIUM;
                  const isSelected = item.id === selectedHandoffId;
                  const hasUnread = unreadIds.has(item.id);
                  const isItemMine = item.assignedToId === myId;
                  const assignee = item.assignedTo;
                  const msgs = item.conversation?.messages ?? [];
                  const lastUserMsg = msgs.find(m => m.senderType === 'USER');
                  const { s: ws, color: wc } = waitLabel(item.createdAt);
                  return (
                    <button key={item.id} id={`inbox-item-${item.id}`}
                      onClick={() => setSelectedHandoffId(item.id)}
                      style={{
                        width: '100%', textAlign: 'left', padding: '10px 12px 8px',
                        border: 'none', borderBottom: '1px solid #1a2035',
                        background: isSelected ? '#1a2240' : hasUnread ? '#12172b' : 'transparent',
                        cursor: 'pointer', transition: 'background 0.1s',
                        borderLeft: `3px solid ${isSelected ? '#6366f1' : p.dot}`,
                      }}>
                      {/* Row 1: priority + wait + assignee */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
                        {hasUnread && <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#6366f1', flexShrink: 0 }} />}
                        <span style={{ fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 4, background: p.bg, color: p.dot, border: `1px solid ${p.dot}40` }}>{p.label.toUpperCase()}</span>
                        <span style={{ fontSize: 9, fontWeight: 700, color: wc }}>⏱ {ws}</span>
                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
                          {item.status === 'RESOLVED'
                            ? <span style={{ fontSize: 9, color: '#34d399', display: 'flex', alignItems: 'center', gap: 2 }}><CheckCircle2 size={8} /> Done</span>
                            : assignee
                              ? <><Avatar name={assignee.name} email={assignee.email} size={14} color={isItemMine ? '#4f46e5' : '#374151'} /><span style={{ fontSize: 9, color: isItemMine ? '#a5b4fc' : '#64748b' }}>{isItemMine ? 'You' : assignee.name?.split(' ')[0] ?? assignee.email.split('@')[0]}</span></>
                              : <span style={{ fontSize: 9, color: '#ef4444', fontWeight: 600 }}>⚡ Unassigned</span>
                          }
                        </div>
                      </div>
                      {/* Escalation reason */}
                      {item.note && <div style={{ fontSize: 10, color: p.text, marginBottom: 2, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontStyle: 'italic' }}>↳ {item.note}</div>}
                      {/* Last user message */}
                      {lastUserMsg && <div style={{ fontSize: 11, color: hasUnread ? '#cbd5e1' : '#64748b', fontWeight: hasUnread ? 600 : 400, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const, overflow: 'hidden', marginBottom: 4 }}>"{lastUserMsg.content}"</div>}
                      {/* Meta + claim */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 9, color: '#475569' }}>
                        <span>{item.conversation?.externalUserId?.split('@')[0] ?? 'anon'}</span>
                        <span>· {item.conversation?.channel?.replace('_', ' ')}</span>
                        {item.status === 'OPEN' && !assignee && (
                          <button onClick={async e => { e.stopPropagation(); await claimHandoff(item.id); setSelectedHandoffId(item.id); }} style={{ marginLeft: 'auto', padding: '2px 8px', borderRadius: 5, border: '1px solid #4f46e5', background: '#1a1a40', color: '#818cf8', fontSize: 9, fontWeight: 700, cursor: 'pointer' }}>
                            Claim →
                          </button>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* ── CHATS ── */}
          {section === 'chats' && (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {loadingChats && <div style={{ padding: 24, textAlign: 'center' }}><Spinner size="sm" /></div>}
              {allChats.map(chat => {
                const isSelected = chat.id === selectedConvId;
                const isWaiting = chat.status === 'WAITING_FOR_AGENT';
                const hasUnread = unreadChatIds.has(chat.id);
                return (
                  <button key={chat.id} onClick={() => setSelectedConvId(chat.id)} style={{
                    width: '100%', textAlign: 'left', padding: '9px 12px',
                    border: 'none', borderBottom: '1px solid #1a2035',
                    background: isSelected ? '#1a2240' : hasUnread ? '#12172b' : 'transparent',
                    cursor: 'pointer',
                    borderLeft: `3px solid ${isSelected ? '#6366f1' : hasUnread ? '#818cf8' : isWaiting ? '#f97316' : '#334155'}`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
                      {hasUnread && (
                        <span style={{
                          width: 7, height: 7, borderRadius: '50%', background: '#6366f1',
                          flexShrink: 0, animation: 'livePulse 1.5s infinite',
                        }} />
                      )}
                      <Avatar name={chat.externalUserId} email="user" size={20} color={isWaiting ? '#321a06' : '#1e293b'} />
                      <span style={{ fontSize: 11, fontWeight: hasUnread ? 700 : 600, color: hasUnread ? '#c7d2fe' : '#e2e8f0', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', flex: 1 }}>
                        {chat.externalUserId ? chat.externalUserId.split('@')[0] : 'Anonymous'}
                      </span>
                      <span style={{ fontSize: 9, color: hasUnread ? '#818cf8' : '#475569' }}>{new Date(chat.updatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      {hasUnread && <span style={{ fontSize: 9, fontWeight: 800, background: '#6366f120', color: '#818cf8', border: '1px solid #6366f140', borderRadius: 10, padding: '1px 6px' }}>New message</span>}
                      <span style={{ fontSize: 9, color: isWaiting ? '#f97316' : '#34d399', fontWeight: 700 }}>{isWaiting ? '⚡ Needs operator' : chat.status}</span>
                      <span style={{ fontSize: 9, color: '#475569' }}>· {chat.channel.replace('_', ' ')}</span>
                      {chat.agent && <span style={{ fontSize: 9, color: '#475569' }}>· {chat.agent.name}</span>}
                      <span style={{ fontSize: 9, color: '#475569', marginLeft: 'auto' }}>{chat.turnCount} turns</span>
                    </div>
                  </button>
                );
              })}
              {!loadingChats && allChats.length === 0 && <div style={{ padding: '32px 16px', textAlign: 'center', color: '#475569', fontSize: 11 }}><MessageSquare size={20} style={{ opacity: 0.2, display: 'block', margin: '0 auto 8px' }} />No conversations</div>}
            </div>
          )}

          {/* ── APPROVALS ── */}
          {section === 'approvals' && (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {loadingApprovals && <div style={{ padding: 24, textAlign: 'center' }}><Spinner size="sm" /></div>}
              {approvals.map(req => {
                const isSelected = req.id === selectedApprovalId;
                const isPending = req.status === 'PENDING';
                return (
                  <div key={req.id} role="button" tabIndex={0}
                    onClick={() => setSelectedApprovalId(req.id)}
                    onKeyDown={e => e.key === 'Enter' && setSelectedApprovalId(req.id)}
                    style={{
                    width: '100%', textAlign: 'left', padding: '9px 12px',
                    border: 'none', borderBottom: '1px solid #1a2035',
                    background: isSelected ? '#1a2240' : 'transparent', cursor: 'pointer',
                    borderLeft: `3px solid ${isSelected ? '#6366f1' : isPending ? '#f59e0b' : '#334155'}`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
                      <span style={{ fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 4, background: isPending ? '#2a1f06' : '#111827', color: isPending ? '#f59e0b' : '#64748b', border: `1px solid ${isPending ? '#f59e0b40' : '#334155'}` }}>{req.status}</span>
                      <span style={{ marginLeft: 'auto', fontSize: 9, color: '#475569' }}>{new Date(req.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    {req.userMessage && <div style={{ fontSize: 11, color: '#64748b', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const, overflow: 'hidden' }}>"{req.userMessage}"</div>}
                    {isPending && (
                      <div style={{ display: 'flex', gap: 5, marginTop: 5 }}>
                        <button onClick={async e => { e.stopPropagation(); await approveRequest(req.id); }} style={{ flex: 1, padding: '3px 0', borderRadius: 5, border: '1px solid #10b98130', background: '#0d2d22', color: '#34d399', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>✓ Approve</button>
                        <button onClick={async e => { e.stopPropagation(); await rejectRequest(req.id); }} style={{ flex: 1, padding: '3px 0', borderRadius: 5, border: '1px solid #ef444430', background: '#3a0d0d', color: '#f87171', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>✕ Reject</button>
                      </div>
                    )}
                  </div>
                );
              })}
              {!loadingApprovals && approvals.length === 0 && <div style={{ padding: '32px 16px', textAlign: 'center', color: '#475569', fontSize: 11 }}><CheckCircle2 size={20} style={{ opacity: 0.2, display: 'block', margin: '0 auto 8px' }} />No approvals</div>}
            </div>
          )}
        </div>

        {/* ══════════════ MIDDLE — context panel ══════════════ */}
        <div style={{ display: 'flex', flexDirection: 'column', background: '#0d1117', borderRight: '1px solid #1e2840', minHeight: 0, overflowY: 'auto' }}>

          {selectedHandoff ? (
            <>
              {/* Customer info */}
              <div style={{ padding: '14px 12px 10px', borderBottom: '1px solid #1e2840' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Customer</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <Avatar name={selectedHandoff.conversation?.externalUserId} email="user" size={28} color="#1e3a5f" />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0' }}>{selectedHandoff.conversation?.externalUserId ?? 'Anonymous'}</div>
                    <div style={{ fontSize: 10, color: '#475569' }}>{selectedHandoff.conversation?.channel?.replace('_', ' ')}</div>
                  </div>
                </div>
                {pri && (
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' as const }}>
                    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: pri.bg, color: pri.dot, border: `1px solid ${pri.dot}40`, fontWeight: 700 }}>{pri.label}</span>
                    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: '#1e2840', color: '#64748b' }}>{(() => { const { s, color } = waitLabel(selectedHandoff.createdAt); return <span style={{ color }}>⏱ {s}</span>; })()}</span>
                  </div>
                )}
                {selectedHandoff.note && (
                  <div style={{ marginTop: 8, padding: '7px 10px', borderRadius: 7, background: pri ? `${pri.bg}cc` : '#1e2840', border: `1px solid ${pri ? `${pri.dot}25` : '#2d3f5f'}`, fontSize: 11, color: pri?.text ?? '#94a3b8', lineHeight: 1.5 }}>
                    <span style={{ fontWeight: 700 }}>Reason: </span>{selectedHandoff.note}
                  </div>
                )}
              </div>

              {/* Assignee */}
              <div style={{ padding: '10px 12px', borderBottom: '1px solid #1e2840' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 7 }}>Assigned to</div>
                {selectedHandoff.assignedTo ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Avatar name={selectedHandoff.assignedTo.name} email={selectedHandoff.assignedTo.email} size={22} color={isMine ? '#6366f1' : '#374151'} />
                    <span style={{ fontSize: 11, color: isMine ? '#a5b4fc' : '#94a3b8', fontWeight: 600 }}>{isMine ? 'You' : (selectedHandoff.assignedTo.name ?? selectedHandoff.assignedTo.email)}</span>
                    {!isMine && !isResolved && <button onClick={() => claimHandoff(selectedHandoff.id)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontSize: 10, fontWeight: 600 }}>Take over</button>}
                  </div>
                ) : !isResolved ? (
                  <button onClick={() => claimHandoff(selectedHandoff.id)} style={{ width: '100%', padding: '5px 10px', borderRadius: 7, border: '1px solid #6366f140', background: '#6366f115', color: '#a5b4fc', cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'center' }}>
                    <UserPlus size={12} /> Claim handoff
                  </button>
                ) : <span style={{ fontSize: 11, color: '#34d399', display: 'flex', alignItems: 'center', gap: 5 }}><CheckCircle2 size={12} /> Resolved</span>}
              </div>

              {/* AI Activity timeline (events only) */}
              {handoffEvents.length > 0 && (
                <div style={{ padding: '10px 12px', borderBottom: '1px solid #1e2840' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>AI Activity</div>
                  {handoffEvents.map(event => <EventAccordion key={event.id} event={event} />)}
                </div>
              )}

              {/* Operator Actions */}
              {operatorTools.length > 0 && (
                <OperatorActionsPanel tools={operatorTools} />
              )}
            </>
          ) : convDetail ? (
            /* Chat context panel */
            <div style={{ padding: '14px 12px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Conversation</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Avatar name={convDetail.externalUserId} email="user" size={28} color="#1e3a5f" />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0' }}>{convDetail.externalUserId ?? 'Anonymous'}</div>
                  <div style={{ fontSize: 10, color: '#475569' }}>{convDetail.channel.replace('_', ' ')} · {convDetail.agent?.name}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' as const, marginBottom: 10 }}>
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: convDetail.status === 'OPEN' ? '#0d2d22' : '#321a06', color: convDetail.status === 'OPEN' ? '#34d399' : '#f97316', border: `1px solid ${convDetail.status === 'OPEN' ? '#10b98120' : '#f9731620'}`, fontWeight: 700 }}>{convDetail.status}</span>
                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: '#1e2840', color: '#64748b' }}>{convDetail.turnCount} turns</span>
              </div>
              {convDetail.events.length > 0 && (
                <>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>AI Activity</div>
                  {convDetail.events.map(event => <EventAccordion key={event.id} event={event} />)}
                </>
              )}
            </div>
          ) : selectedApproval ? (
            <div style={{ padding: '14px 12px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Approval Request</div>
              <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.6 }}>Created {new Date(selectedApproval.createdAt).toLocaleString('en-GB')}</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Expires {new Date(selectedApproval.expiresAt).toLocaleString('en-GB')}</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, color: '#1e2840', gap: 8 }}>
              <Tag size={28} style={{ opacity: 0.3 }} />
              <p style={{ margin: 0, fontSize: 11, color: '#334155' }}>Select an item to see context</p>
            </div>
          )}
        </div>

        {/* ══════════════ RIGHT — thread / detail ══════════════ */}
        {section === 'handoffs' && selectedHandoff && pri ? (
          <div style={{ display: 'flex', flexDirection: 'column', background: '#0d1117', flex: 1, minHeight: 0 }}>
            {/* Topbar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: '1px solid #1e2840', background: '#0f1221', flexShrink: 0 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: pri.dot, flexShrink: 0 }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>{selectedHandoff.conversation?.externalUserId ?? 'Customer'}</span>
              <span style={{ fontSize: 11, color: '#475569' }}>· {timeline.filter(t => t.kind === 'msg').length} messages</span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                {!isResolved && (
                  <button
                    id={`release-${selectedHandoff.id}`}
                    onClick={() => releaseHandoff(selectedHandoff.id)}
                    title="Release back to AI — the customer won't see any change"
                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 7, border: '1px solid #6366f130', background: '#1a1040', color: '#a5b4fc', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                  >
                    <Bot size={12} /> Release to AI
                  </button>
                )}
                {!isResolved ? (
                  <button id={`resolve-${selectedHandoff.id}`} onClick={() => resolveHandoff(selectedHandoff.id)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 7, border: '1px solid #10b98130', background: '#10b98110', color: '#34d399', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                    <CheckCircle2 size={12} /> Resolve
                  </button>
                ) : <span style={{ fontSize: 11, color: '#34d399', display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircle2 size={12} /> Resolved</span>}
              </div>
            </div>
            {isAssignedToOther && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 16px', background: '#1a1a2e', borderBottom: '1px solid #2d3f5f', fontSize: 11, color: '#94a3b8' }}>
                <UserCheck size={12} style={{ color: '#6366f1' }} />
                Assigned to <strong style={{ color: '#a5b4fc', marginLeft: 3 }}>{selectedHandoff.assignedTo?.name ?? selectedHandoff.assignedTo?.email ?? 'another operator'}</strong>
                <button onClick={() => claimHandoff(selectedHandoff.id)} style={{ marginLeft: 4, background: 'none', border: 'none', cursor: 'pointer', color: '#6366f1', fontWeight: 600, fontSize: 11, padding: 0 }}>— take over</button>
              </div>
            )}

            {/* Merged timeline */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px 10px' }}>
              {timeline.map((item, i) =>
                item.kind === 'msg'
                  ? <SenderBubble key={item.id ?? i} msg={item} />
                  : <EventAccordion key={item.id ?? i} event={item} />
              )}
              <div ref={bottomRef} />
            </div>

            {/* Reply */}
            {!isResolved ? (
              <div style={{ borderTop: '1px solid #1e2840', background: '#0f1221', flexShrink: 0 }}>

                {/* ── AI Assist draft banner ── */}
                {assistDraft && (
                  <div style={{ margin: '10px 14px 0', padding: '12px 14px', background: 'linear-gradient(135deg, #1a1040, #0f1a2e)', border: '1px solid #6366f140', borderRadius: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Sparkles size={13} style={{ color: '#a5b4fc' }} />
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#a5b4fc' }}>AI Draft</span>
                        {assistChunks > 0 && (
                          <span style={{ fontSize: 10, color: '#475569', background: '#1e2840', padding: '1px 6px', borderRadius: 8 }}>
                            {assistChunks} KB source{assistChunks !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                        {/* Send immediately */}
                        <button
                          id="draft-send-btn"
                          onClick={sendDraftDirectly}
                          disabled={sending}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, border: 'none', background: '#4f46e5', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                        >
                          <Send size={10} /> Send
                        </button>
                        {/* Put in textarea to edit */}
                        <button
                          id="draft-edit-btn"
                          onClick={() => { setReply(assistDraft); setAssistDraft(null); }}
                          style={{ padding: '4px 9px', borderRadius: 6, border: '1px solid #6366f140', background: 'transparent', color: '#a5b4fc', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                        >
                          Edit
                        </button>
                        {/* Dismiss */}
                        <button
                          onClick={() => setAssistDraft(null)}
                          style={{ padding: '4px 6px', borderRadius: 6, border: '1px solid #1e2840', background: 'transparent', color: '#334155', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        >
                          <X size={11} />
                        </button>
                      </div>
                    </div>
                    <p style={{ margin: 0, fontSize: 12, color: '#94a3b8', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{assistDraft}</p>
                  </div>
                )}

                {/* ── Reply input ── */}
                <div style={{ padding: '10px 14px', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  {platformUser && <Avatar name={platformUser.name} email={platformUser.email} size={26} color="#4f46e5" />}
                  <textarea id="operator-reply-input" value={reply} onChange={e => setReply(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                    placeholder="Reply to customer… (Enter to send)" rows={2}
                    style={{ flex: 1, resize: 'none', padding: '8px 10px', background: '#161b2e', border: '1px solid #2d3f5f', borderRadius: 9, color: '#e2e8f0', fontSize: 13, lineHeight: 1.5, outline: 'none', fontFamily: 'inherit' }} />

                  {/* AI Assist trigger */}
                  <button
                    id="ai-assist-btn"
                    onClick={handleAssist}
                    disabled={assisting}
                    title="Ask AI to draft a reply using skills & knowledge base"
                    style={{ padding: '8px 10px', borderRadius: 9, border: '1px solid #6366f140', background: '#1a1040', color: assisting ? '#475569' : '#a5b4fc', cursor: assisting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, flexShrink: 0, transition: 'all 0.15s' }}
                  >
                    {assisting ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={13} />}
                    {assisting ? '' : 'Solve'}
                  </button>

                  <button id="send-reply-btn" onClick={sendReply} disabled={sending || !reply.trim()} style={{ padding: '8px 14px', borderRadius: 9, border: 'none', background: reply.trim() ? '#4f46e5' : '#1e2840', color: reply.trim() ? '#fff' : '#475569', cursor: reply.trim() && !sending ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600, flexShrink: 0, boxShadow: reply.trim() ? '0 2px 14px #4f46e540' : 'none' }}>
                    {sending ? <Spinner size="sm" /> : <Send size={13} />} Send
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ padding: '12px 16px', borderTop: '1px solid #1e2840', background: '#0d1117', textAlign: 'center', fontSize: 11, color: '#475569' }}>Conversation resolved.</div>
            )}
          </div>
        ) : section === 'chats' && convDetail ? (
          <div style={{ display: 'flex', flexDirection: 'column', background: '#0d1117', flex: 1, minHeight: 0 }}>
            <div style={{ padding: '10px 16px', borderBottom: '1px solid #1e2840', background: '#0f1221', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>{convDetail.externalUserId ?? 'Anonymous'}</span>
              {!convDetail.handoff && (
                <button
                  onClick={() => setShowHandoffForm(v => !v)}
                  style={{ marginLeft: 'auto', padding: '4px 10px', borderRadius: 7, border: '1px solid #f9731630', background: showHandoffForm ? '#4a2506' : '#321a06', color: '#fdba74', cursor: 'pointer', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <AlertTriangle size={11} /> Create handoff
                </button>
              )}
              {convDetail.handoff && (
                <span style={{ marginLeft: 'auto', fontSize: 10, color: '#34d399', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <CheckCircle2 size={10} /> Handoff active
                </span>
              )}
            </div>
            {showHandoffForm && !convDetail.handoff && (
              <div style={{ padding: '10px 16px', background: '#161b2e', borderBottom: '1px solid #2d3f5f', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, color: '#64748b', marginBottom: 4, fontWeight: 600 }}>Reason (optional)</div>
                  <input
                    value={handoffNote}
                    onChange={e => setHandoffNote(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') createHandoffFromChat(); if (e.key === 'Escape') setShowHandoffForm(false); }}
                    placeholder="e.g. Customer requesting human agent…"
                    autoFocus
                    style={{ width: '100%', padding: '6px 10px', background: '#0f1221', border: '1px solid #2d3f5f', borderRadius: 7, color: '#e2e8f0', fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <button onClick={createHandoffFromChat} disabled={creatingHandoff} style={{ padding: '6px 14px', borderRadius: 7, border: 'none', background: '#f97316', color: '#fff', fontSize: 11, fontWeight: 700, cursor: creatingHandoff ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                  {creatingHandoff ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <AlertTriangle size={11} />}
                  {creatingHandoff ? 'Creating…' : 'Escalate'}
                </button>
                <button onClick={() => setShowHandoffForm(false)} style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid #334155', background: 'transparent', color: '#64748b', fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>Cancel</button>
              </div>
            )}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px 10px' }}>
              {(() => {
                // Merge DB messages with locally-appended realtime messages, dedup by id
                const base = convDetail.messages ?? [];
                const live = localMessages[selectedConvId!] ?? [];
                const merged = [...base, ...live]
                  .filter((m, i, arr) => arr.findIndex(x => x.id === m.id) === i)
                  .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
                return merged.map(m => <SenderBubble key={m.id} msg={m as HandoffMessage} />);
              })()}
            </div>
            <div style={{ padding: '10px 16px', borderTop: '1px solid #1e2840', background: '#0f1221', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              {convDetail.handoff ? (
                <button
                  onClick={() => {
                    const h = allHandoffs.find(x => x.conversationId === selectedConvId);
                    if (h) { setSection('handoffs'); setSelectedHandoffId(h.id); }
                    else { qc.invalidateQueries({ queryKey: ['inbox-handoffs'] }); setSection('handoffs'); }
                  }}
                  style={{ padding: '7px 16px', borderRadius: 8, border: 'none', background: '#10b981', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <CheckCircle2 size={12} /> Go to handoff to reply →
                </button>
              ) : (
                <span style={{ fontSize: 10, color: '#475569' }}>Read-only · Use "Create handoff" above to reply</span>
              )}
            </div>
          </div>
        ) : section === 'approvals' && selectedApproval ? (
          <div style={{ display: 'flex', flexDirection: 'column', background: '#0d1117', padding: 24, gap: 16 }}>
            <div style={{ padding: '16px 20px', borderRadius: 12, background: '#1a1f33', border: '1px solid #2d3f5f' }}>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>Skill execution approval</div>
              {selectedApproval.userMessage && <div style={{ padding: '10px 14px', borderRadius: 8, background: '#1e293b', border: '1px solid #334155', fontSize: 13, color: '#e2e8f0', lineHeight: 1.6, marginBottom: 12, fontStyle: 'italic' }}>"{selectedApproval.userMessage}"</div>}
              {selectedApproval.status === 'PENDING' && (
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => approveRequest(selectedApproval.id)} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: '#10b981', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><CheckCircle2 size={14} /> Approve</button>
                  <button onClick={() => rejectRequest(selectedApproval.id)} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><XCircle size={14} /> Reject</button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0d1117', color: '#2d3f5f', gap: 10 }}>
            <Inbox size={36} style={{ opacity: 0.1 }} />
            <p style={{ margin: 0, fontSize: 12, color: '#475569' }}>
              {section === 'handoffs' ? 'Select a handoff to respond' : section === 'chats' ? 'Select a conversation to view' : 'Select an approval request'}
            </p>
          </div>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } @keyframes livePulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.4;transform:scale(1.4)} }`}</style>
    </div>
  );
}
